import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../layouts/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { toast } from 'sonner';
import { Loader2, UserPlus, CheckCircle, Copy } from 'lucide-react';
import { authAPI, dropdownAPI } from '../lib/api';

export default function Register() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [credentials, setCredentials] = useState(null);
  const navigate = useNavigate();

  // Form data
  const [formData, setFormData] = useState({
    company_code_no: '',
    mobile_no: '',
    user_position: '',
    email: '',
  });

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    try {
      const response = await dropdownAPI.getUserPositions();
      setPositions(response.data);
    } catch (error) {
      console.error('Failed to fetch positions:', error);
    }
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_code_no || !formData.mobile_no || !formData.user_position) {
      toast.error('Please fill all required fields');
      return;
    }

    // Move to email step
    setStep(2);
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.register(formData);
      setCredentials(response.data);
      setStep(3);
      toast.success('Registration successful!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (step === 3 && credentials) {
    return (
      <AuthLayout>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight">Welcome to MyTour!</CardTitle>
            <CardDescription>
              Your account has been created successfully
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <AlertTitle className="text-blue-800">Your Login Credentials</AlertTitle>
              <AlertDescription className="text-blue-700 mt-2 space-y-2">
                <div className="flex items-center justify-between bg-white p-2 rounded">
                  <span>Username: <strong>{credentials.username}</strong></span>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(credentials.username)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center justify-between bg-white p-2 rounded">
                  <span>Password: <strong>{credentials.password}</strong></span>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(credentials.password)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </AlertDescription>
            </Alert>

            {credentials.email_sent && (
              <p className="text-sm text-slate-600 text-center">
                Credentials have also been sent to your email.
              </p>
            )}

            <p className="text-sm text-slate-600 text-center">
              Please save these credentials and change your password after first login.
            </p>

            <Button
              className="w-full"
              onClick={() => navigate('/login')}
              data-testid="go-to-login"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold tracking-tight">Create an account</CardTitle>
          <CardDescription>
            {step === 1 ? 'Enter your BBEST employee details' : 'Enter your email address'}
          </CardDescription>
          
          {/* Progress indicator */}
          <div className="flex items-center gap-2 pt-4">
            <div className={`h-2 flex-1 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-slate-200'}`} />
            <div className={`h-2 flex-1 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-slate-200'}`} />
          </div>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <form onSubmit={handleStep1Submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_code">Company Code No *</Label>
                <Input
                  id="company_code"
                  type="text"
                  placeholder="e.g., FM001"
                  value={formData.company_code_no}
                  onChange={(e) => setFormData({ ...formData, company_code_no: e.target.value })}
                  className="bg-slate-50"
                  data-testid="register-company-code"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number *</Label>
                <Input
                  id="mobile"
                  type="text"
                  placeholder="e.g., 9876543210"
                  value={formData.mobile_no}
                  onChange={(e) => setFormData({ ...formData, mobile_no: e.target.value })}
                  className="bg-slate-50"
                  data-testid="register-mobile"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="position">User Position *</Label>
                <Select
                  value={formData.user_position}
                  onValueChange={(value) => setFormData({ ...formData, user_position: value })}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="register-position">
                    <SelectValue placeholder="Select your position" />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((pos) => (
                      <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" className="w-full" data-testid="register-next">
                Next
              </Button>
            </form>
          ) : (
            <form onSubmit={handleStep2Submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-slate-50"
                  disabled={loading}
                  data-testid="register-email"
                />
                <p className="text-xs text-slate-500">
                  Your login credentials will be sent to this email
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Register
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
